/**
 * DataGrid Adapter for Manufacturers
 * Transforms Manufacturer Field Registry into MUI DataGrid column definitions
 */

import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Manufacturer } from '../../../services/api/domains/manufacturers/types';
import { MANUFACTURER_FIELDS_REGISTRY } from '../fields';
import {
  getGridFilterOperatorsForType,
  getAsyncSearchFilterOperators,
} from '../../../utils/fieldOperators';
import {
  getContextAwareConfigFromRegistry,
  toStandardAsyncSelectConfig,
} from '../../../shared/utils/contextAwareAsyncSelectConfig';

/**
 * Convert Manufacturer Field Registry to DataGrid column definitions
 * @returns Array of GridColDef for MUI DataGrid
 */
export function toDataGridColumns(): GridColDef<Manufacturer>[] {
  return MANUFACTURER_FIELDS_REGISTRY.map(field => {
    // Determine the filter operator type
    let filterOperatorType: 'string' | 'number' | 'date' = 'string';
    if (field.dataType === 'array') {
      filterOperatorType = 'string';
    } else if (field.dataType === 'number') {
      filterOperatorType = 'number';
    } else if (field.dataType === 'date') {
      filterOperatorType = 'date';
    } else {
      // For boolean, object, and string types, use string operators
      filterOperatorType = 'string';
    }

    // For relational array fields, use flattened field name for filtering
    // e.g., "facilities" -> "facility_name" for backend compatibility
    let fieldName = field.key as string;
    if (
      field.isRelation &&
      field.dataType === 'array' &&
      field.relationConfig
    ) {
      fieldName = field.relationConfig.displayField;
    }

    const column: GridColDef<Manufacturer> = {
      field: fieldName,
      headerName: field.label,
      description: field.description,
      width: field.renderConfig?.width || 150,
      flex: field.renderConfig?.flex,
      sortable: field.isSortable ?? true,
      filterable: field.isFilterable ?? true,
      hideable: true,
    };

    // Determine filter operators based on field type
    if (field.isFilterable) {
      // For relations with API endpoint, use async search operators (check first)
      if (
        field.isRelation &&
        field.relationConfig?.apiEndpoint &&
        field.relationConfig?.searchField
      ) {
        // Get context-aware config for DataGrid filter context
        // Filter context uses searchField (name) for both value and search
        const contextConfig = getContextAwareConfigFromRegistry(
          MANUFACTURER_FIELDS_REGISTRY,
          field.key as string,
          'filter' // ✅ Filter context uses name field
        );

        if (contextConfig) {
          const asyncConfig = toStandardAsyncSelectConfig(contextConfig);
          column.filterOperators = getAsyncSearchFilterOperators(
            asyncConfig.apiEndpoint,
            asyncConfig.searchField,
            asyncConfig.valueField, // Uses name field for filter value
            asyncConfig.labelField
          );
        } else {
          // Fallback to standard operators
          column.filterOperators =
            getGridFilterOperatorsForType(filterOperatorType);
        }
      } else {
        // Use standard filter operators for non-async fields
        column.filterOperators =
          getGridFilterOperatorsForType(filterOperatorType);
      }
    }

    // Add custom renderCell for special cases
    if (field.dataType === 'date') {
      // Date fields - format with toLocaleString()
      column.renderCell = (params: GridRenderCellParams<Manufacturer>) => {
        const value = params.value;
        return value ? new Date(value as string).toLocaleString() : '';
      };
    } else if (field.isComputed && field.computedFrom) {
      // Computed count fields - calculate from source array
      column.valueGetter = (_value, row) => {
        const sourceArray = row[field.computedFrom as keyof Manufacturer];
        return Array.isArray(sourceArray) ? sourceArray.length : 0;
      };
    } else if (
      field.key === 'products' &&
      field.isRelation &&
      field.relationConfig
    ) {
      // Products relation - show preferred names with fallback
      // Note: field name is 'product_name' but we need to access 'products' array from row
      column.valueGetter = (_value, row) => {
        return row.products;
      };
      column.renderCell = (params: GridRenderCellParams<Manufacturer>) => {
        const products = params.value as Array<{
          product_name: string;
          preferred_product_name?: string | null;
        }>;
        if (!Array.isArray(products) || products.length === 0) return '—';

        const relationConfig = field.relationConfig;
        if (!relationConfig) return '—';

        const { preferredDisplayField, displayField } = relationConfig;
        const names = products.map(
          p =>
            (preferredDisplayField &&
              p[preferredDisplayField as keyof typeof p]) ||
            p[displayField as keyof typeof p]
        );

        const maxDisplay = field.renderConfig?.maxDisplayItems || 3;
        const visible = names.slice(0, maxDisplay);
        const remaining = names.length - maxDisplay;

        return (
          <span>
            {visible.join(', ')}
            {remaining > 0 && ` (+${remaining} more)`}
          </span>
        );
      };
    } else if (
      field.key === 'facilities' &&
      field.isRelation &&
      field.relationConfig
    ) {
      // Facilities relation - show names
      // Note: field name is 'facility_name' but we need to access 'facilities' array from row
      column.valueGetter = (_value, row) => {
        return row.facilities;
      };
      column.renderCell = (params: GridRenderCellParams<Manufacturer>) => {
        const facilities = params.value as Array<Record<string, unknown>>;
        if (!Array.isArray(facilities) || facilities.length === 0) return '—';

        const relationConfig = field.relationConfig;
        if (!relationConfig) return '—';

        const displayField = relationConfig.displayField;
        const names = facilities.map(f => f[displayField]);

        const maxDisplay = field.renderConfig?.maxDisplayItems || 3;
        const visible = names.slice(0, maxDisplay);
        const remaining = names.length - maxDisplay;

        return (
          <span>
            {visible.join(', ')}
            {remaining > 0 && ` (+${remaining} more)`}
          </span>
        );
      };
    }

    return column;
  });
}

/**
 * Get default column visibility model
 * Hides metadata and relational fields by default
 */
export function getDefaultColumnVisibility(): Record<string, boolean> {
  const visibility: Record<string, boolean> = {};

  MANUFACTURER_FIELDS_REGISTRY.forEach(field => {
    // Hide metadata fields and products/facilities by default
    const hideByDefault = [
      'user_id',
      'created_date',
      'modified_date',
      'products',
      'facilities',
    ].includes(field.key as string);

    visibility[field.key as string] = !hideByDefault;
  });

  return visibility;
}
